import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import axiosInstance from "../../../services/api";
import statsService from "../../../services/statsService";
import { formatCount, formatShortDate, getOrderNumber } from "../../../helpers/functions";
import { RevAlert, RevButton, RevCard, RevChip, RevEmpty, RevField, RevFigure, RevKvRow, RevModal, RevPagination, RevSegmented, RevSkeleton } from "../../../components/ds";

const DEFAULT_LIMIT = 20;

// Mientras la disputa esté viva Stripe no deja reembolsar: el dinero ya está retenido.
const OPEN_DISPUTES = ['warning_needs_response', 'needs_response', 'under_review'];

// Tooltip del chip de disputa: el texto completo y, si aún se puede actuar, dónde hacerlo.
const disputeTitle = (t, status) => OPEN_DISPUTES.includes(status)
    ? `${t(`event.payments.dispute.${status}`)}. ${t('event.payments.dispute.hint')}`
    : t(`event.payments.dispute.${status}`);

const REFUND_ERROR_KEY = {
    charge_disputed: 'event.payments.refundDisabled.disputed',
    account_disconnected: 'event.payments.refundDisabled.disconnected',
};

// Filas del esqueleto de la tabla. NO es `DEFAULT_LIMIT` (20): el límite es un tope de la
// consulta, no una previsión de cuántos pedidos hay, y un esqueleto que sobra hace que el
// contenido colapse hacia arriba al cargar — justo el salto que el esqueleto evita. Se acota
// a lo que cabe en pantalla: a 1440x900 caben 9 filas enteras sobre la línea del pliegue con
// la banda de Stripe en el cromo y 10 sin ella (medido en el banco, `filas-sin-scroll.txt`),
// así que 8 deja una fila de margen en el peor caso y nunca pasa del pliegue. Si sobra, la
// página CRECE al llegar los datos y no tira de lo que ya se ve.
const FILAS_ESQUELETO = 8;

// Forma de la tabla de pedidos para el esqueleto de carga: las seis celdas de la fila real
// (fecha en dos líneas, fotos, importe, cliente en dos líneas, pedido, estado) y las clases
// de esta pantalla. Lo consume también el esqueleto de página de `eventContainer`, para que
// el literal de las clases viva en un solo sitio.
export const ESQUELETO_PAGOS = {
    filas: FILAS_ESQUELETO,
    celdas: ['stack', 'line', 'line', 'stack', 'line', 'chip'],
    clases: { superficie: 'rev-pay-table', cabecera: 'rev-pay-head', fila: 'rev-pay-row rev-pay-row--inerte' },
};

const EventPayments = ({ eventCode, user }) => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [limit, setLimit] = useState(DEFAULT_LIMIT);
    const [activeSearch, setActiveSearch] = useState('');
    const [activeLimit, setActiveLimit] = useState(DEFAULT_LIMIT);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [actionResult, setActionResult] = useState(null);
    const [downloadLinks, setDownloadLinks] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [minOverlapPercent, setMinOverlapPercent] = useState('');
    const [refundType, setRefundType] = useState('full');
    const [refundAmount, setRefundAmount] = useState('');
    const [numberOfOrders, setNumberOfOrders] = useState(null);
    const [totalAmountOrders, setTotalAmountOrders] = useState(null);

    const locale = i18n.language;
    const currencyFormatter = useMemo(() => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }), [locale]);

    const normalizePayment = (p) => {
        return {
            id: p.orderId,
            amount: p.orderAmount,
            currency: 'eur',
            date: new Date(p.lastUpdated),
            status: p.orderStatus === 'PAID' ? 'succeeded' : p.orderStatus.toLowerCase(),
            orderId: p.orderId,
            orderCounter: p.orderCounter,
            orderType: p.orderType,
            eventName: p.eventName,
            customerEmail: p.customerEmail,
            customerName: p.customerName,
            customerRegistrationEmail: p.customerRegistrationEmail,
            customerId: p.customerId,
            refundedAmount: p.refundedAmount || 0,
            refundableAmount: p.refundableAmount ?? p.orderAmount,
            disputeStatus: p.disputeStatus ?? null,
            refundableFromRevopic: p.refundableFromRevopic !== false,
            rawData: p
        };
    };

    // El backend devuelve los pedidos por fecha descendente. Con `page` responde
    // { items, total }; un backend anterior responde solo el array de la primera página.
    const fetchPayments = useCallback((searchValue = '', limitValue = DEFAULT_LIMIT, pageValue = 0) => {
        if (!id) return;
        setLoadingPayments(true);
        setError(null);
        setActiveSearch(searchValue);
        setActiveLimit(limitValue);
        setPage(pageValue);

        const params = new URLSearchParams();
        params.append('limit', limitValue);
        params.append('page', pageValue);
        if (searchValue) {
            params.append('search', searchValue);
        }

        axiosInstance.get(`/orders-payments/${id}?${params.toString()}`)
            .then(res => {
                const data = res.data || [];
                const items = Array.isArray(data) ? data : (data.items || []);
                setPayments(items.map(normalizePayment));
                setTotal(Array.isArray(data) ? null : data.total);
            })
            .catch(err => {
                setError(err.message);
            })
            .finally(() => {
                setLoadingPayments(false);
            });
    }, [id]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    useEffect(() => {
        let alive = true;
        if (id) {
            statsService.getNumberOfOrders(id).then((response) => {
                const value = Number(response?.data?.Data);
                if (alive) setNumberOfOrders(Number.isNaN(value) ? null : value);
            }).catch(() => {});
            statsService.getTotalAmountOrders(id).then((response) => {
                const value = Number(response?.data?.Data);
                if (alive) setTotalAmountOrders(Number.isNaN(value) ? null : value);
            }).catch(() => {});
        }
        return () => {
            alive = false;
        };
        // eslint-disable-next-line
    }, [id]);

    const handleSearch = () => {
        fetchPayments(search, limit);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = Number(e.target.value);
        setLimit(newLimit);
        fetchPayments(activeSearch, newLimit);
    };

    const handlePageChange = (newPage) => {
        fetchPayments(activeSearch, activeLimit, newPage);
    };

    const openModal = (payment) => {
        setSelectedPayment(payment);
        setActionResult(null);
        setDownloadLinks(null);
        setConfirmAction(null);
        setMinOverlapPercent('');
        setRefundType('full');
        setRefundAmount('');
    };

    const closeModal = () => {
        setSelectedPayment(null);
        setActionResult(null);
        setDownloadLinks(null);
        setActionLoading(null);
        setConfirmAction(null);
        setMinOverlapPercent('');
        setRefundType('full');
        setRefundAmount('');
    };

    const handleResendEmail = async () => {
        setActionLoading('resend');
        setActionResult(null);
        try {
            const res = await axiosInstance.post(`/orders-payments/${id}/resend-email`, {
                orderId: selectedPayment.orderId
            });
            const data = res.data;
            const emailDiffers = data.email && data.email !== selectedPayment.customerEmail;
            setActionResult({
                type: 'success',
                message: emailDiffers
                    ? `${data.message}. ${t('event.payments.result.sentTo')}: ${data.email} (${t('event.payments.result.registrationEmail')}: ${selectedPayment.customerEmail})`
                    : `${data.message} → ${data.email}`
            });
        } catch (err) {
            setActionResult({ type: 'error', message: err.message });
        } finally {
            setActionLoading(null);
        }
    };

    const handleGetDownloadLinks = async () => {
        setActionLoading('download');
        setActionResult(null);
        setDownloadLinks(null);
        try {
            const res = await axiosInstance.get(
                `/orders-payments/${id}/download-links?orderId=${encodeURIComponent(selectedPayment.orderId)}`
            );
            setDownloadLinks(res.data.downloadUrl);
        } catch (err) {
            setActionResult({ type: 'error', message: err.message });
        } finally {
            setActionLoading(null);
        }
    };

    const handleMarkPurchased = async () => {
        setActionLoading('mark');
        setActionResult(null);
        try {
            const body = { customerId: selectedPayment.customerId };
            if (minOverlapPercent !== '') {
                body.minOverlapPercent = Number(minOverlapPercent);
            }
            const res = await axiosInstance.post(`/orders-payments/${id}/mark-purchased`, body);
            const data = res.data;
            setActionResult({
                type: 'success',
                message: `${data.photosMarkedAsPurchased} ${t('event.payments.result.photosMarked')} (${data.totalPhotosFound} ${t('event.payments.result.found')}, ${data.photosAddedToOrder} ${t('event.payments.result.addedToOrder')})`
            });
        } catch (err) {
            setActionResult({ type: 'error', message: err.message });
        } finally {
            setActionLoading(null);
        }
    };

    const handleRefund = async () => {
        setActionLoading('refund');
        setActionResult(null);
        try {
            const body = { orderId: selectedPayment.orderId };
            if (refundType === 'partial' && refundAmount !== '') {
                body.amount = parseFloat(refundAmount);
            }
            const res = await axiosInstance.post(`/orders-payments/${id}/refund`, body);
            const data = res.data;
            setActionResult({
                type: 'success',
                message: `${t('event.payments.result.refundDone')}: €${data.amount?.toFixed(2)} → ${data.customerEmail || selectedPayment.customerEmail || '-'}`
            });
            setSelectedPayment(prev => ({
                ...prev,
                status: data.orderStatus === 'PAID' ? 'succeeded' : data.orderStatus.toLowerCase(),
                refundedAmount: data.refundedAmount ?? (prev.refundedAmount + (data.amount || 0)),
                refundableAmount: data.refundableAmount ?? (prev.amount - (data.refundedAmount ?? (prev.refundedAmount + (data.amount || 0)))),
            }));
            setRefundType('full');
            setRefundAmount('');
            fetchPayments(activeSearch, activeLimit, page);
        } catch (err) {
            const code = err.response?.data?.code;
            const errorMsg = REFUND_ERROR_KEY[code]
                ? t(REFUND_ERROR_KEY[code])
                : (err.response?.data?.error || err.message);
            setActionResult({ type: 'error', message: errorMsg });
        } finally {
            setActionLoading(null);
        }
    };

    const isCoupon = (payment) => !payment.amount || payment.amount === 0;

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) return t('event.payments.coupon');
        return currencyFormatter.format(amount);
    };

    const formatDate = (date) => {
        if (!date) return { time: '-', date: '' };
        return {
            time: date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
            date: formatShortDate(date, locale),
        };
    };

    const getStatusInfo = (payment) => {
        if (payment.refundedAmount > 0 && payment.refundableAmount <= 0) {
            return { key: 'refunded', tone: 'warn' };
        }
        if (payment.refundedAmount > 0 && payment.refundableAmount > 0) {
            return { key: 'partialRefund', tone: 'warn' };
        }
        return { key: 'charged', tone: 'ok' };
    };

    const couponCount = payments.filter(isCoupon).length;
    const refundedCount = payments.filter((p) => p.refundedAmount > 0).length;
    const shownCount = payments.length;
    const firstShown = page * activeLimit + 1;
    const totalCount = shownCount === 0
        ? null
        : total !== null
            ? total
            : activeSearch
                ? (shownCount < activeLimit ? shownCount : null)
                : (numberOfOrders === null ? null : Math.max(numberOfOrders, shownCount));
    const pages = total === null ? 1 : Math.ceil(total / activeLimit);

    return (
        <div>
            <div className="rev-cols rev-cols--main-wide rev-cols--flush">
                <div className="rev-cols__main">

                    <div className="rev-toolbar">
                        <label className="rev-search">
                            <span className="material-icons rev-icon-sm rev-icon-muted">search</span>
                            <input
                                className="rev-search__input"
                                type="text"
                                placeholder={t('event.payments.search.placeholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </label>
                        <RevButton variant="primary" onClick={handleSearch}>
                            {t('event.payments.search.button')}
                        </RevButton>
                        <select className="rev-input rev-pagesize" value={limit} onChange={handleLimitChange}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="rev-toolbar__actions rev-help">
                            {totalCount === null ? (
                                <Trans i18nKey="event.payments.footer.count" count={shownCount} components={[<span className="rev-num" key="0" />]} />
                            ) : (
                                <Trans i18nKey="event.payments.footer.range" count={totalCount} values={{ from: firstShown, to: firstShown + shownCount - 1 }} components={[<span className="rev-num" key="0" />, <span className="rev-num" key="1" />]} />
                            )}
                        </span>
                    </div>

                    {loadingPayments ? (
                        <RevSkeleton.Cuerpo
                            tipo="tabla"
                            {...ESQUELETO_PAGOS}
                            filas={Math.min(activeLimit, FILAS_ESQUELETO)}
                            label={t('event.payments.title')} />
                    ) : error ? (
                        <RevAlert tone="err">
                            <span>{t('event.payments.error')}: {error}</span>
                        </RevAlert>
                    ) : payments.length === 0 ? (
                        <RevEmpty
                            icon={activeSearch ? 'search_off' : 'receipt_long'}
                            title={activeSearch ? t('event.payments.emptySearch.title', { q: activeSearch }) : t('event.payments.empty')}
                            body={activeSearch ? t('event.payments.emptySearch.body') : t('event.payments.empty.body')}
                        />
                    ) : (
                        <div className="rev-pay-table">
                            <div className="rev-pay-head">
                                <span>{t('event.payments.table.date')}</span>
                                <span>{t('event.payments.table.photos')}</span>
                                <span className="rev-pay-cell--right">{t('event.payments.table.amount')}</span>
                                <span>{t('event.payments.table.customer')}</span>
                                <span>{t('event.payments.table.order')}</span>
                                <span className="rev-pay-cell--right">{t('event.payments.table.status')}</span>
                            </div>
                            {payments.map((p, idx) => {
                                const statusInfo = getStatusInfo(p);
                                return (
                                    <div className="rev-pay-row" key={p.id || idx} onClick={() => openModal(p)}>
                                        <div className="rev-pay-cell--stack">
                                            <span className="rev-num">{formatDate(p.date).time}</span>
                                            <span className="rev-num rev-help">{formatDate(p.date).date}</span>
                                        </div>
                                        <div className="rev-pay-cell--row">
                                            <span className="rev-num rev-pay-photos__count">{p.orderCounter || 0}</span>
                                            {p.orderType === 'pack' && (
                                                <RevChip>{t('event.payments.type.pack')}</RevChip>
                                            )}
                                        </div>
                                        <div className="rev-pay-cell--right">
                                            <span className={`rev-num${isCoupon(p) ? ' rev-pay-amount--coupon' : ''}`}>
                                                {formatCurrency(p.amount)}
                                            </span>
                                            {p.refundedAmount > 0 && (
                                                <span className="rev-num rev-help rev-help--warn">
                                                    −{currencyFormatter.format(p.refundedAmount)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="rev-pay-cell--stack">
                                            {p.customerName && (
                                                <span className="rev-pay-cell--ellipsis">{p.customerName}</span>
                                            )}
                                            <span className="rev-pay-cell--ellipsis rev-help">
                                                {p.customerEmail || p.customerRegistrationEmail || '-'}
                                            </span>
                                        </div>
                                        <span className="rev-mono rev-pay-cell--ellipsis">{getOrderNumber(p.orderId)}</span>
                                        <div className="rev-pay-cell--right">
                                            <RevChip tone={statusInfo.tone}>
                                                {t(`event.payments.status.${statusInfo.key}`)}
                                            </RevChip>
                                            {p.disputeStatus && (
                                                <RevChip tone="err" title={disputeTitle(t, p.disputeStatus)}>
                                                    {t(`event.payments.disputeShort.${p.disputeStatus}`)}
                                                </RevChip>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {!loadingPayments && !error && (
                        <RevPagination className="rev-pay-pager" page={page} pages={pages} onChange={handlePageChange}
                            prevLabel={t('event.payments.pagination.prev')} nextLabel={t('event.payments.pagination.next')} />
                    )}
                </div>

                <div className="rev-cols__aside">
                    <RevCard titleAs="h3" title={t('event.payments.summary.title')}>
                        {numberOfOrders === null
                            ? <RevKvRow k={t('event.payments.summary.charged')} v="—" muted />
                            : <RevKvRow k={t('event.payments.summary.charged')} v={formatCount(numberOfOrders, locale)} num />}
                        <RevKvRow k={t('event.payments.summary.coupon')} v={formatCount(couponCount, locale)} num />
                        <RevKvRow k={t('event.payments.summary.refunded')} v={formatCount(refundedCount, locale)} num />
                        <span className="rev-help">{t('event.payments.summary.footnote')}</span>
                        <div className="rev-summary-total">
                            <span className="rev-summary-total__k">{t('event.payments.summary.total')}</span>
                            {totalAmountOrders === null
                                ? <RevFigure size="sm" muted>—</RevFigure>
                                : <RevFigure size="sm">{currencyFormatter.format(totalAmountOrders)}</RevFigure>}
                        </div>
                    </RevCard>
                    <RevCard titleAs="h3" title={t('event.payments.helpCard.title')}>
                        <span className="rev-help">{t('event.payments.helpCard.body')}</span>
                        <span className="rev-help rev-help--divider">{t('event.payments.helpCard.refundNote')}</span>
                    </RevCard>
                </div>
            </div>

            {selectedPayment && (() => {
                const statusInfo = getStatusInfo(selectedPayment);
                const disputeOpen = OPEN_DISPUTES.includes(selectedPayment.disputeStatus);
                const refundBlocked = disputeOpen
                    ? t('event.payments.refundDisabled.disputed')
                    : selectedPayment.refundableFromRevopic === false
                        ? t('event.payments.refundDisabled.disconnected')
                        : '';
                const actions = [
                    {
                        key: 'resend',
                        icon: 'email',
                        label: t('event.payments.action.resend.label'),
                        detail: t('event.payments.action.resend.detail'),
                        loadingLabel: t('event.payments.action.resend.loading'),
                        onClick: () => setConfirmAction('resend'),
                    },
                    {
                        key: 'download',
                        icon: 'download',
                        label: t('event.payments.action.download.label'),
                        detail: t('event.payments.action.download.detail'),
                        loadingLabel: t('event.payments.action.download.loading'),
                        onClick: () => setConfirmAction('download'),
                    },
                    {
                        key: 'mark',
                        icon: 'check_circle',
                        label: t('event.payments.action.mark.label'),
                        detail: selectedPayment.orderType !== 'pack'
                            ? t('event.payments.action.mark.packOnly')
                            : t('event.payments.action.mark.detail'),
                        loadingLabel: t('event.payments.action.loading'),
                        onClick: () => setConfirmAction('mark'),
                        disabled: selectedPayment.orderType !== 'pack',
                        title: selectedPayment.orderType !== 'pack' ? t('event.payments.action.mark.packOnly') : '',
                    },
                    selectedPayment.amount > 0 && selectedPayment.refundableAmount > 0 ? {
                        key: 'refund',
                        icon: 'undo',
                        label: t('event.payments.action.refund.label'),
                        detail: refundBlocked || t('event.payments.action.refund.detail'),
                        loadingLabel: t('event.payments.action.loading'),
                        onClick: () => setConfirmAction('refund'),
                        danger: true,
                        disabled: Boolean(refundBlocked),
                        title: refundBlocked,
                    } : null,
                ].filter(Boolean);

                return (
                    <RevModal
                        width={520}
                        onClose={closeModal}
                        closeLabel={t('event.payments.modal.close')}
                        footer={
                            <RevButton onClick={closeModal}>
                                {t('event.payments.modal.close')}
                            </RevButton>
                        }
                    >
                            <div className="rev-card__head">
                                <div className="rev-pay-cell--stack">
                                    <span className="rev-label">{t('event.payments.modal.orderLabel')}</span>
                                    <span className="rev-mono">#{getOrderNumber(selectedPayment.orderId)}</span>
                                </div>
                                <RevChip tone={statusInfo.tone}>
                                    {t(`event.payments.status.${statusInfo.key}`)}
                                </RevChip>
                            </div>

                            <div className="rev-row">
                                <div className="rev-pay-cell--stack">
                                    <span className="rev-label">{t('event.payments.table.customer')}</span>
                                    {selectedPayment.customerName && (
                                        <span>{selectedPayment.customerName}</span>
                                    )}
                                    <span className="rev-help">
                                        {selectedPayment.customerEmail || selectedPayment.customerRegistrationEmail || '-'}
                                    </span>
                                </div>
                                <div className="rev-pay-cell--stack">
                                    <span className="rev-label">{t('event.payments.table.amount')}</span>
                                    <span className={`rev-num${isCoupon(selectedPayment) ? ' rev-pay-amount--coupon' : ''}`}>
                                        {formatCurrency(selectedPayment.amount)}
                                    </span>
                                    {selectedPayment.refundedAmount > 0 && (
                                        <span className="rev-num rev-help rev-help--warn">
                                            {t('event.payments.modal.refunded')}: {currencyFormatter.format(selectedPayment.refundedAmount)}
                                        </span>
                                    )}
                                    {selectedPayment.refundedAmount > 0 && selectedPayment.refundableAmount > 0 && (
                                        <span className="rev-num rev-help rev-help--warn">
                                            {t('event.payments.modal.available')}: {currencyFormatter.format(selectedPayment.refundableAmount)}
                                        </span>
                                    )}
                                    {selectedPayment.disputeStatus && (
                                        <RevChip tone="err" title={disputeTitle(t, selectedPayment.disputeStatus)}>
                                            {t(`event.payments.dispute.${selectedPayment.disputeStatus}`)}
                                        </RevChip>
                                    )}
                                </div>
                                <div className="rev-pay-cell--stack">
                                    <span className="rev-label">{t('event.payments.table.date')}</span>
                                    <span className="rev-num">{formatDate(selectedPayment.date).time}</span>
                                    <span className="rev-num rev-help">{formatDate(selectedPayment.date).date}</span>
                                </div>
                                <div className="rev-pay-cell--stack">
                                    <span className="rev-label">{t('event.payments.table.photos')}</span>
                                    <div className="rev-pay-cell--row">
                                        <span className="rev-num">{selectedPayment.orderCounter || 0}</span>
                                        {selectedPayment.orderType === 'pack' && (
                                            <RevChip>{t('event.payments.type.pack')}</RevChip>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {selectedPayment.amount > 0 && selectedPayment.refundableAmount === 0 && selectedPayment.refundedAmount > 0 && (
                                <RevAlert tone="warn">
                                    <span>
                                        {t('event.payments.modal.fullyRefunded')} ({currencyFormatter.format(selectedPayment.refundedAmount)})
                                    </span>
                                </RevAlert>
                            )}

                            <div className="rev-pay-cell--stack">
                                <span className="rev-label">{t('event.payments.modal.actions')}</span>
                                {actions.map((action) => {
                                    const isActive = actionLoading === action.key;
                                    return (
                                        <button
                                            key={action.key}
                                            type="button"
                                            className={`rev-action-row rev-action-row--multiline${action.danger ? ' rev-action-row--danger' : ''}`}
                                            onClick={action.onClick}
                                            disabled={!!actionLoading || action.disabled}
                                            title={action.title || ''}
                                        >
                                            {isActive ? (
                                                <span className="rev-spin" />
                                            ) : (
                                                <span className="material-icons rev-icon-sm">{action.icon}</span>
                                            )}
                                            <span className="rev-pay-action">
                                                <span>{isActive ? action.loadingLabel : action.label}</span>
                                                <span className="rev-help">{action.detail}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {actionResult && (
                                <RevAlert tone={actionResult.type === 'success' ? 'ok' : 'err'}>
                                    <span>{actionResult.message}</span>
                                </RevAlert>
                            )}

                            {downloadLinks && (
                                <RevCard>
                                    <div className="rev-card__head">
                                        <span className="rev-label">{t('event.payments.modal.downloadLink')}</span>
                                        <div className="rev-pay-cell--row">
                                            <RevButton size="sm" variant="secondary" onClick={() => window.open(downloadLinks, '_blank')}>
                                                <span className="material-icons rev-icon-sm">open_in_new</span>
                                                {t('event.payments.modal.open')}
                                            </RevButton>
                                            <RevButton size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(downloadLinks)}>
                                                <span className="material-icons rev-icon-sm">content_copy</span>
                                                {t('event.payments.modal.copy')}
                                            </RevButton>
                                        </div>
                                    </div>
                                </RevCard>
                            )}
                    </RevModal>
                );
            })()}

            {confirmAction && (() => {
                const confirmConfig = {
                    resend: {
                        title: t('event.payments.action.resend.label'),
                        description: t('event.payments.confirm.resend.description'),
                        confirmText: t('event.payments.confirm.resend.button'),
                        onConfirm: () => { setConfirmAction(null); handleResendEmail(); },
                    },
                    download: {
                        title: t('event.payments.action.download.label'),
                        description: t('event.payments.confirm.download.description'),
                        confirmText: t('event.payments.confirm.download.button'),
                        onConfirm: () => { setConfirmAction(null); handleGetDownloadLinks(); },
                    },
                    refund: {
                        title: t('event.payments.action.refund.label'),
                        description: selectedPayment?.refundedAmount > 0
                            ? `${t('event.payments.confirm.refund.available')}: ${currencyFormatter.format(selectedPayment?.refundableAmount || 0)} (${t('event.payments.confirm.refund.of')} ${currencyFormatter.format(selectedPayment?.amount || 0)} ${t('event.payments.confirm.refund.original')})`
                            : t('event.payments.confirm.refund.description'),
                        confirmText: refundType === 'full'
                            ? `${t('event.payments.confirm.refund.button')} ${currencyFormatter.format(selectedPayment?.refundableAmount || 0)}`
                            : t('event.payments.confirm.refund.button'),
                        danger: true,
                        onConfirm: () => {
                            if (refundType === 'partial' && (!refundAmount || parseFloat(refundAmount) <= 0)) return;
                            if (refundType === 'partial' && parseFloat(refundAmount) > selectedPayment?.refundableAmount) return;
                            setConfirmAction(null);
                            handleRefund();
                        },
                        extraContent: (
                            <>
                                <RevSegmented
                                    items={[
                                        { value: 'full', label: t('event.payments.confirm.refund.full') },
                                        { value: 'partial', label: t('event.payments.confirm.refund.partial') },
                                    ]}
                                    value={refundType}
                                    onChange={(value) => {
                                        setRefundType(value);
                                        if (value === 'full') setRefundAmount('');
                                    }}
                                />
                                {refundType === 'partial' && (
                                    <RevField>
                                        <span className="rev-help">
                                            {t('event.payments.confirm.refund.amountHelp')}: {currencyFormatter.format(selectedPayment?.refundableAmount || 0)}
                                        </span>
                                        <div className="rev-field-row">
                                            <input
                                                type="number"
                                                min="0.01"
                                                max={selectedPayment?.refundableAmount}
                                                step="0.01"
                                                placeholder="0.00"
                                                value={refundAmount}
                                                onChange={(e) => setRefundAmount(e.target.value)}
                                                className="rev-input rev-num rev-input--prefixed"
                                            />
                                            <span className="rev-suffix">€</span>
                                        </div>
                                    </RevField>
                                )}
                            </>
                        ),
                    },
                    mark: {
                        title: t('event.payments.action.mark.label'),
                        description: t('event.payments.confirm.mark.description'),
                        confirmText: t('event.payments.confirm.mark.button'),
                        onConfirm: () => { setConfirmAction(null); handleMarkPurchased(); },
                        extraContent: (
                            <RevField label={t('event.payments.confirm.mark.thresholdLabel')}>
                                <span className="rev-help">{t('event.payments.confirm.mark.thresholdHelp')}</span>
                                <div className="rev-field-row">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={minOverlapPercent}
                                        onChange={(e) => setMinOverlapPercent(e.target.value)}
                                        className="rev-input rev-num rev-input--prefixed"
                                    />
                                    <span className="rev-suffix">%</span>
                                </div>
                            </RevField>
                        ),
                    },
                };
                const cfg = confirmConfig[confirmAction];
                if (!cfg) return null;

                return (
                    <RevModal width={420} onClose={() => setConfirmAction(null)} closeLabel={t('event.payments.confirm.cancel')} title={cfg.title}>
                            <span className="rev-help">{cfg.description}</span>
                            {cfg.extraContent || null}
                            <div className="rev-pay-confirm__actions">
                                <RevButton onClick={() => setConfirmAction(null)}>
                                    {t('event.payments.confirm.cancel')}
                                </RevButton>
                                <RevButton variant={cfg.danger ? 'danger' : 'primary'} onClick={cfg.onConfirm}>
                                    {cfg.confirmText}
                                </RevButton>
                            </div>
                    </RevModal>
                );
            })()}
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.usersReducer.user,
});

export default connect(mapStateToProps)(EventPayments);
